import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { levelStyle } from "@/lib/tcfLevel";
import {
  ArrowLeft, ArrowRight, CheckCircle2, XCircle, Headphones, Trophy, Loader2,
  Flag, LayoutGrid, Lightbulb, Check, Clock, Timer, BookOpen, ScrollText, Lock, X, LineChart,
} from "lucide-react";

const MEDIA = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/exams/media/${p}`;
const TIME_LIMIT = { reading: 60 * 60, listening: 35 * 60 };
const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function Palette({ questions, current, answers, flagged, review, reviewMap, onJump, gridClass = "grid-cols-6 sm:grid-cols-8 lg:grid-cols-5" }) {
  return (
    <div data-testid="question-palette">
      <div className="flex items-center gap-2 text-sm font-semibold"><LayoutGrid className="h-4 w-4 text-primary" /> Questions</div>
      <div className={`mt-3 grid ${gridClass} gap-1.5`}>
        {questions.map((q, i) => {
          let cls = "bg-secondary text-muted-foreground";
          if (review) {
            const rd = reviewMap[q.id];
            if (rd?.is_correct) cls = "bg-green-600 text-white";
            else if (rd?.selected) cls = "bg-red-500 text-white";
            else cls = "bg-amber-200 text-amber-900";
          } else {
            if (flagged.has(q.id)) cls = "bg-amber-400 text-amber-950";
            else if (answers[q.id]) cls = "emerald-surface text-white";
          }
          const ring = i === current ? "ring-2 ring-offset-1 ring-primary" : "";
          return (
            <button key={q.id} onClick={() => onJump(i)} data-testid={`palette-${q.number}`}
              className={`h-9 rounded-lg text-xs font-bold transition-all ${cls} ${ring}`}>{q.number}</button>
          );
        })}
      </div>
      {!review && (
        <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded emerald-surface inline-block" /> Answered</div>
          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-amber-400 inline-block" /> Marked for review</div>
          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-secondary inline-block" /> Not answered</div>
        </div>
      )}
    </div>
  );
}

// Compact answer key card — question + options with the correct answer highlighted.
function TranscriptCard({ q, onZoom }) {
  const correct = q.correct_answer;
  // Listening options are just letters ("A"..) — the real choices live in the image.
  const hasTextOpts = (q.options || []).some(
    (o) => (o.text || "").trim() && (o.text || "").trim().toUpperCase() !== (o.id || "").toUpperCase()
  );
  const imgs = q.image_paths || [];
  const generic = /^Question\s*\d+\s*:?\s*(Écoutez|Ecoutez)/i.test(q.question_text || "");
  return (
    <div className="rounded-lg border border-border bg-background p-2.5" data-testid={`transcript-q-${q.number}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="h-5 w-5 grid place-items-center rounded-md emerald-surface text-white text-[11px] font-bold shrink-0">{q.number}</span>
        {q.cefr_level && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${levelStyle(q.cefr_level).chip}`}>{q.cefr_level}</span>}
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-green-600 text-white text-[11px] font-bold px-2 py-0.5"
          data-testid={`transcript-correct-${q.number}`}>
          <CheckCircle2 className="h-3 w-3" /> {correct}
        </span>
      </div>
      {q.question_text && !generic && (
        <p className="text-[13px] font-medium leading-snug mb-1.5">{q.question_text}</p>
      )}
      {imgs.map((p) => (
        <button key={p} type="button" onClick={() => onZoom(MEDIA(p))} title="Tap to enlarge"
          className="block w-full mb-1.5">
          <img src={MEDIA(p)} draggable={false} onContextMenu={(e) => e.preventDefault()} alt={`question ${q.number}`}
            className="w-full rounded-md border border-border max-h-44 object-contain bg-white cursor-zoom-in" />
        </button>
      ))}
      {hasTextOpts ? (
        <div className="space-y-1">
          {q.options.map((o) => {
            const isC = o.id === correct;
            return (
              <div key={o.id}
                className={`flex items-center gap-2 rounded-md px-2 py-1 text-[13px] ${isC ? "bg-green-50 border border-green-500 text-green-900 font-semibold" : "text-muted-foreground"}`}>
                <span className={`h-4 w-4 shrink-0 grid place-items-center rounded text-[10px] font-bold ${isC ? "bg-green-600 text-white" : "border border-border"}`}>{o.id}</span>
                <span className="leading-tight">{o.text}</span>
                {isC && <CheckCircle2 className="h-3.5 w-3.5 text-green-600 ml-auto shrink-0" />}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          {q.options.map((o) => {
            const isC = o.id === correct;
            return (
              <span key={o.id}
                className={`h-7 w-7 grid place-items-center rounded-md text-xs font-bold ${isC ? "bg-green-600 text-white" : "bg-secondary text-muted-foreground"}`}>{o.id}</span>
            );
          })}
          <span className="ml-2 text-[11px] text-muted-foreground">Correct: {correct}</span>
        </div>
      )}
    </div>
  );
}

// Full answer key as a popup over the test — compact, two columns, many questions per view.
function TranscriptModal({ open, onClose, title, questions, loading }) {
  const [zoom, setZoom] = useState(null);
  const noCopy = {
    onContextMenu: (e) => e.preventDefault(),
    onCopy: (e) => e.preventDefault(),
    onCut: (e) => e.preventDefault(),
    onDragStart: (e) => e.preventDefault(),
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { zoom ? setZoom(null) : onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, zoom]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4"
      onClick={onClose} data-testid="transcript-modal">
      <div className="relative w-full max-w-5xl max-h-[94vh] bg-card rounded-2xl border border-border shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <ScrollText className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              <h2 className="font-display text-base sm:text-lg font-semibold leading-tight">Transcript — answer key</h2>
              <p className="text-xs text-muted-foreground truncate">{title}{questions.length ? ` · ${questions.length} questions` : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground"><Lock className="h-3 w-3" /> View only</span>
            <button onClick={onClose} data-testid="close-transcript-btn" aria-label="Close"
              className="h-8 w-8 grid place-items-center rounded-full bg-secondary hover:bg-accent transition-colors"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div {...noCopy} className="flex-1 overflow-y-auto p-3 select-none"
          style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" }}>
          {loading ? (
            <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /><p className="mt-2 text-sm">Loading answer key…</p></div>
          ) : questions.length === 0 ? (
            <p className="py-16 text-center text-muted-foreground text-sm">No questions found.</p>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {questions.map((q) => <TranscriptCard key={q.id} q={q} onZoom={setZoom} />)}
            </div>
          )}
        </div>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" data-testid="transcript-zoom"
          onClick={(e) => { e.stopPropagation(); setZoom(null); }}>
          <img src={zoom} draggable={false} onContextMenu={(e) => e.preventDefault()} alt="enlarged"
            className="max-w-full max-h-full object-contain rounded-lg" />
          <button onClick={(e) => { e.stopPropagation(); setZoom(null); }} aria-label="Close image"
            className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/90 text-black"><X className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}

export default function ExamRunner() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [explanations, setExplanations] = useState({});
  const [explLoading, setExplLoading] = useState({});
  const [started, setStarted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [endsAt, setEndsAt] = useState(null);
  const [timed, setTimed] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const submittedRef = useRef(false);
  const restoredRef = useRef(false);
  const autoSubmitRef = useRef(null);
  const STORAGE_KEY = `aiof_exam_${slug}`;
  const clearSaved = useCallback(() => { try { localStorage.removeItem(STORAGE_KEY); } catch {} }, [STORAGE_KEY]);

  useEffect(() => {
    api.get(`/exams/${slug}`).then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.detail || "Could not load this paper."); navigate("/exams"); });
  }, [slug, navigate]);

  const questions = data?.questions || [];
  const total = questions.length;
  const review = !!result;
  const reviewMap = useMemo(() => Object.fromEntries((result?.review || []).map((r) => [r.id, r])), [result]);
  const answeredCount = Object.keys(answers).length;
  const q = questions[current];

  const pick = (qid, optId) => setAnswers((a) => ({ ...a, [qid]: optId }));
  const toggleFlag = (qid) => setFlagged((f) => { const n = new Set(f); n.has(qid) ? n.delete(qid) : n.add(qid); return n; });
  const jump = (i) => { setCurrent(i); setShowPalette(false); window.scrollTo({ top: 0 }); };

  const openTranscript = async () => {
    setShowTranscript(true);
    if (transcriptData) return;
    setTranscriptLoading(true);
    try {
      const r = await api.get(`/exams/${slug}?mode=study`);
      setTranscriptData(r.data.questions || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the transcript."); }
    finally { setTranscriptLoading(false); }
  };

  const loadExplanation = useCallback(async (qid) => {
    if (explanations[qid] || explLoading[qid]) return;
    setExplLoading((s) => ({ ...s, [qid]: true }));
    try {
      const r = await api.post(`/exams/${slug}/questions/${qid}/explain`);
      setExplanations((s) => ({ ...s, [qid]: r.data.explanation }));
    } catch { toast.error("Could not load explanation."); }
    finally { setExplLoading((s) => ({ ...s, [qid]: false })); }
  }, [slug, explanations, explLoading]);

  useEffect(() => { if (review && q) loadExplanation(q.id); }, [review, q, loadExplanation]);

  const submit = async (force = false) => {
    if (submittedRef.current || submitting) return;
    if (!force && answeredCount < total && !window.confirm(`You've answered ${answeredCount}/${total}. Submit anyway? Unanswered questions are marked wrong.`)) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const duration_seconds = startedAt ? Math.round((Date.now() - startedAt) / 1000) : null;
      const r = await api.post(`/exams/${slug}/submit`, { answers, duration_seconds });
      clearSaved();
      setResult(r.data); setCurrent(0); setSecondsLeft(null); window.scrollTo({ top: 0 });
    } catch {
      submittedRef.current = false; // allow retry — answers stay saved in localStorage
      toast.error("Couldn't submit. Your answers are saved — check your connection and tap Submit again.");
    } finally { setSubmitting(false); }
  };
  // Always points at the latest submit so the interval below never captures stale answers.
  autoSubmitRef.current = () => { if (!submittedRef.current) submit(true); };

  const beginExam = () => {
    submittedRef.current = false;
    setStarted(true);
    const now = Date.now();
    setStartedAt(now);
    if (timed) { const lim = TIME_LIMIT[data.test.skill] || 3600; setEndsAt(now + lim * 1000); setSecondsLeft(lim); }
    else { setEndsAt(null); setSecondsLeft(null); }
    window.scrollTo({ top: 0 });
  };

  // Restore an in-progress attempt (survives refresh / accidental navigation / crash).
  useEffect(() => {
    if (!data || restoredRef.current || started || review) return;
    restoredRef.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      setAnswers(s.answers || {});
      setFlagged(new Set(s.flagged || []));
      setCurrent(Math.min(s.current || 0, (data.questions || []).length - 1));
      setStartedAt(s.startedAt || Date.now());
      setTimed(!!s.timed);
      setEndsAt(s.endsAt ?? null);
      setStarted(true);
      toast.info("Resumed your in-progress exam — your answers were saved.");
    } catch {}
  }, [data, started, review, STORAGE_KEY]);

  // Persist progress on every change.
  useEffect(() => {
    if (!started || review) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, flagged: [...flagged], current, startedAt, endsAt, timed }));
    } catch {}
  }, [answers, flagged, current, started, review, startedAt, endsAt, timed, STORAGE_KEY]);

  // Keep the session alive during long exams so audio + submit never 401 (raw <audio> can't use the axios interceptor).
  useEffect(() => {
    if (!started || review) return;
    const beat = () => { api.post("/auth/refresh", null, { _skipAuthRefresh: true }).catch(() => {}); };
    beat();
    const id = setInterval(beat, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, [started, review]);

  // Absolute-deadline timer: robust to backgrounded-tab throttling; auto-submits when the deadline passes.
  useEffect(() => {
    if (!started || review || !timed || !endsAt) return;
    const tick = () => {
      const left = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0 && !submittedRef.current) { toast.info("Time's up — submitting your paper."); autoSubmitRef.current && autoSubmitRef.current(); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [started, review, timed, endsAt]); // eslint-disable-line

  const timeLow = secondsLeft != null && secondsLeft <= 300;

  if (!data) return <div className="text-muted-foreground" data-testid="exam-loading">Loading paper…</div>;

  const skill = data.test.skill;
  const isListening = skill === "listening";
  const limit = TIME_LIMIT[skill] || 3600;
  const limitMin = Math.round(limit / 60);

  const transcriptModal = (
    <TranscriptModal open={showTranscript} onClose={() => setShowTranscript(false)}
      title={data.test.title} questions={transcriptData || []} loading={transcriptLoading} />
  );

  // Intro / start screen
  if (!started && !review) {
    const SkillIcon = isListening ? Headphones : BookOpen;
    return (
      <div data-testid="exam-runner-page">
        <Link to={`/exams?skill=${skill}`} data-testid="back-to-bank" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> All papers</Link>
        <div className="mt-4 max-w-xl mx-auto rounded-2xl bg-card border border-border p-6 sm:p-8 text-center" data-testid="exam-intro">
          <div className="h-14 w-14 rounded-2xl emerald-surface grid place-items-center mx-auto"><SkillIcon className="h-7 w-7 text-white" /></div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-4">{data.test.title}</h1>
          <p className="text-muted-foreground mt-1">{total} questions · {isListening ? "Listening" : "Reading"} · instant CLB scoring</p>

          <div className="mt-6 rounded-xl bg-background border border-border p-4 flex items-center justify-between gap-4 text-left">
            <div>
              <p className="font-semibold flex items-center gap-2"><Timer className="h-4 w-4 text-primary" /> Timed mode</p>
              <p className="text-sm text-muted-foreground mt-0.5">{timed ? `${limitMin} min countdown — auto-submits at 0 (real exam feel).` : "No timer — practise at your own pace."}</p>
            </div>
            <button role="switch" aria-checked={timed} onClick={() => setTimed((v) => !v)} data-testid="timed-toggle"
              className={`relative h-7 w-12 rounded-full transition-colors shrink-0 ${timed ? "bg-primary" : "bg-secondary"}`}>
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${timed ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>

          <button onClick={beginExam} data-testid="start-exam-btn"
            className="mt-6 w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold inline-flex items-center justify-center gap-2">
            {timed ? <><Timer className="h-4 w-4" /> Start {limitMin}-min test</> : "Start untimed test"}
          </button>

          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" /> OR <span className="h-px flex-1 bg-border" /></div>
          <button onClick={openTranscript} data-testid="view-transcript-btn-intro"
            className="w-full h-12 rounded-xl bg-amber-100 text-amber-900 border border-amber-300 font-semibold inline-flex items-center justify-center gap-2">
            <ScrollText className="h-4 w-4" /> View transcript
          </button>
          <p className="text-xs text-muted-foreground mt-2">Opens the full answer key — every question with its options and the correct answer highlighted, on one page.</p>
        </div>
        {transcriptModal}
      </div>
    );
  }

  // Active test-taking screen — big single-view layout (matches TCF exam feel)
  if (!review) {
    const opts = q.options || [];
    const selected = answers[q.id];
    const hasImg = (q.image_paths || []).length > 0;
    const hasAudio = (q.audio_paths || []).length > 0;
    const lv = q.cefr_level;
    return (
      <div data-testid="exam-runner-page">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <Link to={`/exams?skill=${skill}`} data-testid="back-to-bank" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> All papers</Link>
          <div className="flex items-center gap-2">
            <button onClick={openTranscript} data-testid="view-transcript-btn" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-amber-100 text-amber-900 border border-amber-300 text-sm font-semibold hover:bg-amber-200 transition-colors"><ScrollText className="h-4 w-4" /> Transcript</button>
            {timed && secondsLeft != null && (
              <span data-testid="exam-timer-mobile" className={`lg:hidden inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm font-bold tabular-nums ${timeLow ? "bg-destructive text-destructive-foreground animate-pulse" : "emerald-surface text-white"}`}><Clock className="h-4 w-4" /> {fmtTime(secondsLeft)}</span>
            )}
            <button onClick={() => setShowPalette(true)} data-testid="open-palette-btn" className="lg:hidden inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-secondary text-sm font-semibold"><LayoutGrid className="h-4 w-4" /> {answeredCount}/{total}</button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[280px_1fr] items-start">
          {/* LEFT SIDEBAR */}
          <div className="hidden lg:flex flex-col gap-4">
            {timed && secondsLeft != null && (
              <div className={`rounded-2xl p-4 text-white ${timeLow ? "bg-destructive animate-pulse" : "emerald-surface"}`} data-testid="exam-timer">
                <div className="flex items-center gap-2.5"><Clock className="h-6 w-6 shrink-0" /><div><div className="font-display text-2xl font-bold tabular-nums leading-none">{fmtTime(secondsLeft)}</div><div className="text-xs text-white/80 mt-1">Time remaining</div></div></div>
                <div className="mt-3 h-1.5 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white transition-all" style={{ width: `${Math.max(0, (secondsLeft / limit) * 100)}%` }} /></div>
              </div>
            )}
            <div className="rounded-2xl bg-card border border-border p-4">
              <Palette questions={questions} current={current} answers={answers} flagged={flagged} review={false} reviewMap={reviewMap} onJump={jump} gridClass="grid-cols-8" />
              <button onClick={submit} disabled={submitting} data-testid="submit-exam-btn-side" className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Submit (${answeredCount}/${total})`}</button>
            </div>
            <Link to={`/exams?skill=${skill}`} data-testid="exit-exam-btn" className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-destructive/40 text-destructive text-sm font-semibold hover:bg-destructive/5"><X className="h-4 w-4" /> Exit exam</Link>
          </div>

          {/* MAIN */}
          <div className="flex flex-col lg:h-[calc(100vh-12rem)]" data-testid={`question-${q.id}`}>
            {/* Question pill + flag */}
            <div className="relative flex items-center justify-center shrink-0">
              <span data-testid="question-number-pill" className="inline-flex items-center gap-2 px-6 h-11 rounded-full border-2 border-primary text-primary font-display font-bold text-lg">Question {current + 1}</span>
              <button onClick={() => toggleFlag(q.id)} data-testid="flag-btn" className={`absolute right-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-colors ${flagged.has(q.id) ? "bg-amber-400 text-amber-950" : "bg-secondary text-muted-foreground hover:bg-accent"}`}><Flag className="h-3.5 w-3.5" /> {flagged.has(q.id) ? "Marked" : "Mark"}</button>
            </div>

            {/* Big passage / media card */}
            <div className="mt-4 flex-1 min-h-0 rounded-3xl border-2 border-primary/30 bg-card p-4 sm:p-6 flex flex-col items-center justify-center gap-3 overflow-hidden">
              {hasAudio && (q.audio_paths).map((p) => (
                <div key={p} className="w-full max-w-lg flex items-center gap-2 shrink-0"><Headphones className="h-5 w-5 text-primary shrink-0" /><audio controls src={MEDIA(p)} className="w-full" data-testid={`q-audio-${q.id}`} /></div>
              ))}
              {hasImg ? (q.image_paths).map((p) => (
                <img key={p} src={MEDIA(p)} onClick={() => setZoomImg(MEDIA(p))} alt={`question ${q.number}`} title="Tap to enlarge"
                  className="max-h-full max-w-full object-contain rounded-xl cursor-zoom-in" data-testid={`q-image-${q.id}`} />
              )) : !hasAudio ? (
                <p className="text-xl sm:text-2xl leading-relaxed text-center max-w-2xl">{q.question_text}</p>
              ) : null}
            </div>

            {/* Question prompt (when the card holds media) */}
            {(hasImg || hasAudio) && q.question_text && (
              <p className="mt-3 text-center font-semibold text-base sm:text-lg shrink-0">{q.question_text}</p>
            )}

            {/* 2 x 2 options */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 shrink-0">
              {opts.map((o) => {
                const sel = o.id === selected;
                return (
                  <button key={o.id} type="button" onClick={() => pick(q.id, o.id)} data-testid={`opt-${q.id}-${o.id}`}
                    className={`text-left px-4 py-3.5 rounded-2xl border-2 transition-colors flex items-center gap-3 ${sel ? "border-primary bg-accent" : "border-border bg-background hover:border-primary"}`}>
                    <span className={`h-8 w-8 shrink-0 grid place-items-center rounded-full text-sm font-bold ${sel ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{o.id}</span>
                    <span className="text-base">{o.text}</span>
                  </button>
                );
              })}
            </div>

            {/* Bottom nav */}
            <div className="mt-4 flex items-center justify-between gap-3 shrink-0">
              <button onClick={() => jump(Math.max(0, current - 1))} disabled={current === 0} data-testid="prev-btn" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full border border-border bg-card text-sm font-semibold disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Previous</button>
              {lv && <span data-testid="question-cefr" className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${levelStyle(lv).chip}`}>{lv}{q.points ? ` · ${q.points} pts` : ""}</span>}
              {current < total - 1 ? (
                <button onClick={() => jump(current + 1)} data-testid="next-btn" className="inline-flex items-center gap-1.5 px-6 h-11 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Next <ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button onClick={submit} disabled={submitting} data-testid="submit-exam-btn" className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit paper"}</button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile palette slide-over */}
        {showPalette && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setShowPalette(false)} data-testid="palette-overlay">
            <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-card p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <Palette questions={questions} current={current} answers={answers} flagged={flagged} review={false} reviewMap={reviewMap} onJump={jump} />
              <button onClick={submit} disabled={submitting} className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold">Submit ({answeredCount}/{total})</button>
            </div>
          </div>
        )}
        {zoomImg && (
          <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" data-testid="q-image-zoom" onClick={() => setZoomImg(null)}>
            <img src={zoomImg} alt="enlarged" className="max-w-full max-h-full object-contain rounded-lg" />
            <button onClick={() => setZoomImg(null)} aria-label="Close" className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/90 text-black"><X className="h-5 w-5" /></button>
          </div>
        )}
        {transcriptModal}
      </div>
    );
  }

  const rd = review ? reviewMap[q.id] : null;
  const selected = review ? rd?.selected : answers[q.id];
  const correctAnswer = review ? rd?.correct_answer : null;
  const correctText = review ? rd?.correct_answer_text : "";

  return (
    <div data-testid="exam-runner-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={`/exams?skill=${skill}`} data-testid="back-to-bank" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> All papers</Link>
        <div className="flex items-center gap-2">
          <button onClick={openTranscript} data-testid="view-transcript-btn"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-amber-100 text-amber-900 border border-amber-300 text-sm font-semibold hover:bg-amber-200 transition-colors">
            <ScrollText className="h-4 w-4" /> Transcript
          </button>
          {!review && timed && secondsLeft != null && (
            <span data-testid="exam-timer" className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-bold tabular-nums ${timeLow ? "bg-destructive text-destructive-foreground animate-pulse" : "emerald-surface text-white"}`}>
              <Clock className="h-4 w-4" /> {fmtTime(secondsLeft)}
            </span>
          )}
          <button onClick={() => setShowPalette(true)} data-testid="open-palette-btn" className="lg:hidden inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-secondary text-sm font-semibold">
            <LayoutGrid className="h-4 w-4" /> {review ? "Questions" : `${answeredCount}/${total}`}
          </button>
        </div>
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-2">{data.test.title}</h1>

      {review && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-2xl emerald-surface grain text-white p-5 sm:p-6" data-testid="exam-result">
          <div className="flex items-center gap-3"><Trophy className="h-6 w-6" /><h2 className="font-display text-xl font-semibold">Your result</h2></div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><div className="text-2xl sm:text-3xl font-display font-semibold" data-testid="result-699">{result.score_699}<span className="text-base font-normal">/699</span></div><div className="text-white/80 text-xs sm:text-sm">Official TCF score</div></div>
            <div><div className="text-2xl sm:text-3xl font-display font-semibold" data-testid="result-level">{result.label}</div><div className="text-white/80 text-xs sm:text-sm">Estimated level</div></div>
            <div><div className="text-2xl sm:text-3xl font-display font-semibold" data-testid="result-score">{result.correct}/{result.total}</div><div className="text-white/80 text-xs sm:text-sm">Correct answers</div></div>
            <div><div className="text-2xl sm:text-3xl font-display font-semibold">{result.pct}%</div><div className="text-white/80 text-xs sm:text-sm">Accuracy</div></div>
          </div>
          {result.bands && (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="result-bands">
              {["A1", "A2", "B1", "B2", "C1", "C2"].map((lvl) => {
                const b = result.bands[lvl]; if (!b) return null;
                return (
                  <div key={lvl} className="rounded-lg bg-white/10 px-2 py-1.5 text-center" data-testid={`result-band-${lvl}`}>
                    <div className="text-[11px] font-bold">{lvl}</div>
                    <div className="text-xs text-white/90">{b.correct}/{b.total}</div>
                  </div>
                );
              })}
            </div>
          )}
          <Link to={`/exams/progress?skill=${skill}`} data-testid="result-progress-link"
            className="mt-4 inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-white text-primary text-sm font-semibold">
            <LineChart className="h-4 w-4" /> View my progress
          </Link>
        </motion.div>
      )}

      <div className="mt-5 grid gap-6 items-start lg:grid-cols-[1fr_240px]">
        {/* Question panel */}
        <div className="rounded-2xl bg-card border border-border p-5 sm:p-6 min-h-[60vh] flex flex-col" data-testid={`question-${q.id}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 grid place-items-center rounded-full emerald-surface text-white text-xs font-bold">{q.number}</span>
              <span className="text-sm text-muted-foreground">Question {current + 1} of {total}</span>
              {(() => { const lv = review ? rd?.cefr_level : q.cefr_level; return lv ? <span data-testid="question-cefr" className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${levelStyle(lv).chip}`}>{lv}</span> : null; })()}
              {review && (rd?.is_correct ? <span className="text-xs font-semibold text-green-600 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Correct</span> : <span className="text-xs font-semibold text-red-500">Incorrect</span>)}
            </div>
            {!review && (
              <button onClick={() => toggleFlag(q.id)} data-testid="flag-btn"
                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${flagged.has(q.id) ? "bg-amber-400 text-amber-950" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
                <Flag className="h-3.5 w-3.5" /> {flagged.has(q.id) ? "Marked" : "Mark for review"}
              </button>
            )}
          </div>

          <div className="mt-4 flex-1 overflow-y-auto max-h-[52vh] pr-1">
            {(q.image_paths || []).map((p) => (
              <img key={p} src={MEDIA(p)} alt={`question ${q.number}`} className="mb-3 rounded-xl border border-border max-w-full max-h-[42vh] object-contain" data-testid={`q-image-${q.id}`} />
            ))}
            {(q.audio_paths || []).map((p) => (
              <div key={p} className="mb-3 flex items-center gap-2"><Headphones className="h-4 w-4 text-primary" /><audio controls src={MEDIA(p)} className="w-full" data-testid={`q-audio-${q.id}`} /></div>
            ))}
            <p className="font-medium leading-snug">{q.question_text}</p>
            <div className="mt-3 space-y-2">
              {q.options.map((o) => {
                let cls = "border-border bg-background hover:border-primary";
                if (review) {
                  if (o.id === correctAnswer) cls = "border-green-600 bg-green-50";
                  else if (o.id === selected) cls = "border-red-500 bg-red-50";
                  else cls = "border-border bg-background opacity-70";
                } else if (o.id === selected) cls = "border-primary bg-accent";
                return (
                  <button key={o.id} type="button" disabled={review} onClick={() => pick(q.id, o.id)} data-testid={`opt-${q.id}-${o.id}`}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-colors flex items-center gap-3 ${cls}`}>
                    <span className="h-6 w-6 shrink-0 grid place-items-center rounded-full border-2 border-current text-xs font-bold">{o.id}</span>
                    <span className="text-sm">{o.text}</span>
                    {review && o.id === correctAnswer && <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto shrink-0" />}
                    {review && o.id === selected && o.id !== correctAnswer && <XCircle className="h-4 w-4 text-red-500 ml-auto shrink-0" />}
                  </button>
                );
              })}
            </div>

            {review && (
              <div className="mt-4 rounded-xl bg-green-50 border border-green-200 p-3" data-testid="answer-explanation">
                <p className="text-sm text-green-800"><b>Correct answer: {correctAnswer}</b>{correctText ? ` — ${correctText}` : ""}</p>
                <div className="mt-2 flex items-start gap-2 text-sm text-green-900">
                  <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                  {explanations[q.id] ? <span>{explanations[q.id]}</span>
                    : explLoading[q.id] ? <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating explanation…</span>
                    : <button onClick={() => loadExplanation(q.id)} data-testid="why-btn" className="font-semibold text-primary hover:underline">Why is this correct?</button>}
                </div>
              </div>
            )}
          </div>

          {/* Nav footer */}
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3">
            <button onClick={() => jump(Math.max(0, current - 1))} disabled={current === 0} data-testid="prev-btn"
              className="inline-flex items-center gap-1 px-4 h-10 rounded-full bg-secondary text-sm font-semibold disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Prev</button>
            {current < total - 1 ? (
              <button onClick={() => jump(current + 1)} data-testid="next-btn" className="inline-flex items-center gap-1 px-4 h-10 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Next <ArrowRight className="h-4 w-4" /></button>
            ) : !review ? (
              <button onClick={submit} disabled={submitting} data-testid="submit-exam-btn" className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit paper"}</button>
            ) : (
              <Link to="/score" className="px-4 h-10 grid place-items-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">See progress</Link>
            )}
          </div>
        </div>

        {/* Palette (desktop) */}
        <div className="hidden lg:block sticky top-20 rounded-2xl bg-card border border-border p-5 max-h-[calc(100vh-6rem)] overflow-hidden">
          <Palette questions={questions} current={current} answers={answers} flagged={flagged} review={review} reviewMap={reviewMap} onJump={jump} />
          {!review && (
            <button onClick={submit} disabled={submitting} data-testid="submit-exam-btn-side"
              className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Submit (${answeredCount}/${total})`}</button>
          )}
        </div>
      </div>

      {/* Palette (mobile slide-over) */}
      {showPalette && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setShowPalette(false)} data-testid="palette-overlay">
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-card p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <Palette questions={questions} current={current} answers={answers} flagged={flagged} review={review} reviewMap={reviewMap} onJump={jump} />
            {!review && (
              <button onClick={submit} disabled={submitting} className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold">
                Submit ({answeredCount}/{total})</button>
            )}
          </div>
        </div>
      )}
      {transcriptModal}
    </div>
  );
}
